export const useCart = () => {
    // Cart cookie hardening:
    //  - sameSite: 'lax' so the cookie is sent on top-level navigations
    //    (the buyer follows a link to the storefront with cart preserved)
    //    but blocks cross-site POSTs (no CSRF surface on cart mutation)
    //  - secure: from runtimeConfig.public.cookieSecure (default true).
    //    NOT `process.env.NODE_ENV === 'production'` because that
    //    expression is build-time inlined by Vite/Nitro — a staging
    //    build with NODE_ENV='staging' bakes `secure: false` into
    //    the bundle and the HTTPS browser rejects the cookie. Driving
    //    from runtimeConfig keeps prod + staging working over HTTPS
    //    without conflating the value with the build profile.
    //  - maxAge bounded to 30 days so an abandoned cart on a shared
    //    browser doesn't haunt a future user forever
    //  - path '/' so it's available on every storefront subroute
    //  - domain: `.{activeDomain}` so the cart survives the hop from
    //    an artist subdomain (e.g. `madabeats.psyfeature.dev`) to the
    //    shop subdomain (`shop.psyfeature.dev`) where checkout lives.
    //    Without this the cookie is host-only and the buyer adds an
    //    item on the artist page only to see an empty cart on the
    //    storefront. The tenant-scope leading `.` keeps the cookie
    //    visible only within the tenant's own subdomain tree (it
    //    can't leak to another tenant's apex).
    const tenant = useState<any>('tenant')
    const activeDomain = tenant.value?.activeDomain as string | null | undefined
    // For raw IP / localhost dev, the browser refuses a Domain attribute.
    // Skip in those cases — falls back to host-only, which is fine because
    // dev usually points at a single host anyway.
    const isHostNumericOrLocal = !activeDomain
        || /^(\d{1,3}\.){3}\d{1,3}$/.test(activeDomain)
        || activeDomain === 'localhost'
    const cookieDomain = !isHostNumericOrLocal ? `.${activeDomain}` : undefined

    const cartCookie = useCookie('cart', {
        default: () => [],
        watch: true,
        sameSite: 'lax',
        secure: useRuntimeConfig().public.cookieSecure !== false,
        maxAge: 60 * 60 * 24 * 30,
        path: '/',
        ...(cookieDomain ? { domain: cookieDomain } : {})
    })

    const cart = useState('cart', () => cartCookie.value)

    // Sync state with cookie
    watch(cartCookie, () => {
        cart.value = cartCookie.value
    })

    // Upper bound mirrors the clamp in /api/shop/checkout (1..99). Without it
    // the stepper would happily count past 99, show the buyer a total for the
    // quantity they picked, and then have the server silently bill 99 — a cart
    // that displays one figure and collects another, which is the exact
    // failure the reprice pass exists to prevent.
    const MAX_QUANTITY = 99

    const addToCart = (product) => {
        const existing = cart.value.find(p => p._id === product._id)
        if (!existing) {
            cart.value.push({
                ...product,
                quantity: Math.min(MAX_QUANTITY, Math.max(1, Math.floor(Number(product.quantity) || 1)))
            })
            // Assign a NEW array. `cartCookie.value = cart.value` re-assigns
            // the same reference `cart` was seeded from, so Vue's hasChanged
            // check skips the useCookie watcher and the cookie never persists
            // (cart lost on reload). The merch branch already does this.
            cartCookie.value = [...cart.value]
            const { $toast } = useNuxtApp()
            $toast.success('Added to cart!')
            useShopTracking().addToCart({ id: product._id, name: product.name, priceEur: Number(product.price) || undefined })
        } else if (product.type === 'merch') {
            // For merch, increment quantity if same variant already in cart
            existing.quantity = Math.min(MAX_QUANTITY, (existing.quantity || 1) + (product.quantity || 1))
            cartCookie.value = [...cart.value]
            const { $toast } = useNuxtApp()
            $toast.success('Updated quantity!')
            useShopTracking().addToCart({ id: product._id, name: product.name, priceEur: Number(product.price) || undefined })
        } else {
            const { $toast } = useNuxtApp()
            $toast.info('Item already in cart')
        }
    }

    const updateQuantity = (itemId: string, qty: number) => {
        const item = cart.value.find(p => p._id === itemId)
        if (item) {
            if (qty <= 0) {
                removeFromCart(itemId)
            } else {
                item.quantity = Math.min(MAX_QUANTITY, Math.max(1, Math.floor(qty)))
                cartCookie.value = [...cart.value]
            }
        }
    }

    const removeFromCart = (productId) => {
        const index = cart.value.findIndex(p => p._id === productId)
        if (index !== -1) {
            cart.value.splice(index, 1)
            // New array ref so the useCookie watcher actually fires (see addToCart).
            cartCookie.value = [...cart.value]
            const { $toast } = useNuxtApp()
            $toast.info('Item removed from cart')
        }
    }

    /**
     * Overwrite the stored prices of cart lines from an authoritative source.
     *
     * The cart cookie holds the price that was on screen when the buyer
     * clicked "add", and it lives for 30 days — long enough to outlast any
     * sale window. Checkout always recomputes server-side, so a stale cookie
     * never charges the wrong amount; it DISPLAYS the wrong amount, which is
     * its own problem. `/api/shop/cart/reprice` provides the truth and this
     * writes it back.
     *
     * Returns how many lines actually moved, so the caller can tell the buyer
     * something changed instead of silently swapping the total under them.
     */
    const repriceItems = (updates: Array<{ _id: string; price?: number; listPrice?: number | null; salePercent?: number | null }>) => {
        if (!Array.isArray(updates) || updates.length === 0) return 0
        // `changed` counts PRICE moves (what the buyer is told about);
        // `touched` covers any field write (what forces a cookie save).
        let changed = 0
        let touched = false
        for (const update of updates) {
            if (!update || typeof update._id !== 'string') continue
            if (!Number.isFinite(Number(update.price))) continue
            const item = cart.value.find(p => p._id === update._id)
            if (!item) continue

            const nextPrice = Number(update.price)
            const nextList = update.listPrice ?? null
            const nextPercent = update.salePercent ?? null
            if (Number(item.price) === nextPrice
                && (item.listPrice ?? null) === nextList
                && (item.salePercent ?? null) === nextPercent) continue

            if (Number(item.price) !== nextPrice) changed++
            touched = true
            item.price = nextPrice
            item.listPrice = nextList
            item.salePercent = nextPercent
        }
        // Only when something actually moved. Writing on every cart view would
        // re-stamp the cookie's 30-day maxAge on each visit and churn the
        // useCookie watcher for nothing.
        if (touched) cartCookie.value = [...cart.value]
        return changed
    }

    const clearCart = () => {
        cart.value = []
        cartCookie.value = []
    }

    const total = computed(() => {
        return cart.value.reduce((acc, curr) => acc + curr.price * (curr.quantity || 1), 0).toFixed(2)
    })

    // Sum of quantities so the badge reads "5" when the cart holds 5 shirts
    // on one line, not "1". `cart.value.length` was the line count, which
    // misleads merch buyers.
    const itemCount = computed(() => {
        return cart.value.reduce((acc, item) => acc + (item.quantity || 1), 0)
    })

    // A bundle with merch ships too: it needs the delivery country and a
    // shipping line like any merch item.
    const hasMerch = computed(() => cart.value.some(item => item.type === 'merch' || (item.type === 'bundle' && item.hasMerch)))
    const hasProducts = computed(() => cart.value.some(item => item.type === 'product'))

    return {
        cart,
        addToCart,
        updateQuantity,
        removeFromCart,
        repriceItems,
        clearCart,
        total,
        itemCount,
        hasMerch,
        hasProducts
    }
}
